import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import {
  useMyQueueInvoices,
  useMyQueueQc,
  useMyQueueTasks,
  useMyQueueTaskDetail,
  useMyQueueTimeclock,
  type MyQueueFilter,
} from "../hooks/useMyQueue";
import type {
  MyQueueTask,
  MyQueueTimeclockAction,
} from "../services/myqueueApi";
import { ACTION_LABELS, type TimeclockState } from "../services/timeclockState";
import { consumePendingInAppNav, subscribeInAppNav } from "../../../shared/inAppLinks";
import { emitToast } from "../../../../integrations/tools/outlook/toast-bridge";
import styles from "./MyQueuePanel.module.css";

type View = "tasks" | "timeclock" | "qc" | "invoices";

interface MyQueuePanelProps {
  readonly onBack?: () => void;
}

const FILTERS: ReadonlyArray<{ id: MyQueueFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "scheduled", label: "Scheduled" },
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
];

const CURRENCY_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function formatCurrency(val: number | undefined): string {
  return CURRENCY_FMT.format(typeof val === "number" ? val : 0);
}

function qcStatusClass(status: string): string | undefined {
  if (status === "passed") return styles.statusCompleted;
  if (status === "failed") return styles.qcStatusFailed;
  return styles.statusActive;
}

function getStatusLabel(
  task: MyQueueTask,
): { label: string; className: string } | null {
  if (task.completed) return { label: "Completed", className: styles.statusCompleted! };
  if (task.started) return { label: "Active", className: styles.statusActive! };
  // Prototype shows a "Scheduled" pill only when the task is explicitly
  // scheduled; otherwise no status pill is rendered.
  if (task.scheduled) return { label: "Scheduled", className: styles.statusScheduled! };
  return null;
}

/**
 * Colour the due-date pill by minutes-to-due, mirroring the prototype's
 * buildMqTaskCard thresholds (overdue → red, <15m → orange, <30m → amber,
 * else green). Uses the raw `dueDate` for the calculation, not the display
 * string. Returns "" when there is no parseable due date.
 */
function dueUrgencyClass(task: MyQueueTask): string {
  if (!task.dueDate) return "";
  const due = Date.parse(task.dueDate);
  if (!Number.isFinite(due)) return "";
  const diffMin = (due - Date.now()) / 60_000;
  if (diffMin < 0) return styles.dueRed!;
  if (diffMin < 15) return styles.dueOrange!;
  if (diffMin < 30) return styles.dueAmber!;
  return styles.dueGreen!;
}

interface MyQueueReminders {
  readonly urgent: MyQueueTask[];
  readonly warning: MyQueueTask[];
  readonly overdue: number;
}

/**
 * Classify loaded tasks into due-soon / overdue buckets, deduped by id across
 * the scheduled and in-progress lists — the prototype's mqCheckReminders. We
 * compute from the already-loaded (and 30s-polled) task lists rather than
 * running a separate timer, so this adds no extra network traffic.
 */
function computeReminders(tasks: MyQueueTask[], inProgressTasks: MyQueueTask[]): MyQueueReminders {
  const now = Date.now();
  const seen = new Set<number>();
  const urgent: MyQueueTask[] = [];
  const warning: MyQueueTask[] = [];
  let overdue = 0;
  for (const t of [...tasks, ...inProgressTasks]) {
    if (seen.has(t.id)) continue;
    seen.add(t.id);
    if (t.completed || !t.dueDate) continue;
    const due = Date.parse(t.dueDate);
    if (!Number.isFinite(due)) continue;
    const diffMin = (due - now) / 60_000;
    if (diffMin < 0) overdue++;
    else if (diffMin <= 15) urgent.push(t);
    else if (diffMin <= 30) warning.push(t);
  }
  return { urgent, warning, overdue };
}

function reminderLabel(t: MyQueueTask): string {
  return `#${t.jobNo ?? ""} ${t.jobTask ?? ""}`.trim();
}

function ReminderBanner({ reminders }: { readonly reminders: MyQueueReminders }) {
  const { urgent, warning, overdue } = reminders;
  if (urgent.length > 0) {
    return (
      <output className={`${styles.reminder} ${styles.reminderUrgent}`}>
        ⚠ <strong>{urgent.length} task{urgent.length > 1 ? "s" : ""} due in &lt;15 min:</strong>{" "}
        {urgent.map(reminderLabel).join(", ")}
      </output>
    );
  }
  if (warning.length > 0) {
    return (
      <output className={styles.reminder}>
        ⏰ <strong>{warning.length} task{warning.length > 1 ? "s" : ""} due in &lt;30 min:</strong>{" "}
        {warning.map(reminderLabel).join(", ")}
      </output>
    );
  }
  if (overdue > 0) {
    return (
      <output className={`${styles.reminder} ${styles.reminderUrgent}`}>
        ⚠ <strong>{overdue} overdue task{overdue > 1 ? "s" : ""}</strong>
      </output>
    );
  }
  return null;
}

function getViewLabel(v: View): string {
  if (v === "tasks") return "Tasks";
  if (v === "timeclock") return "Time Clock";
  if (v === "qc") return "QC";
  return "Invoices";
}

/* ── Loading UI ─────────────────────────────── */

function Spinner() {
  return <span className={styles.spinner} aria-hidden="true" />;
}

/** Centred spinner + label; the label doubles as the accessible status text. */
function LoadingRow({ label }: { readonly label: string }) {
  return (
    <output className={styles.loadingRow}>
      <Spinner />
      <span>{label}</span>
    </output>
  );
}

/**
 * Skeleton placeholder cards for first-load states — nicer than a bare
 * "Loading…" and avoids drawing the eye to stale content. `label` is kept
 * screen-reader-only so assistive tech still announces the load.
 */
const SKELETON_KEYS = ["sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6"] as const;

function SkeletonCards({ count = 4, label }: { readonly count?: number; readonly label: string }) {
  return (
    <output className={styles.skeletonList}>
      <span className={styles.srOnly}>{label}</span>
      {SKELETON_KEYS.slice(0, count).map((k) => (
        <div key={k} className={styles.skeletonCard} aria-hidden="true">
          <div className={`${styles.skelLine} ${styles.skelLineWide}`} />
          <div className={`${styles.skelLine} ${styles.skelLineNarrow}`} />
        </div>
      ))}
    </output>
  );
}

export function MyQueuePanel({ onBack }: Readonly<MyQueuePanelProps>) {
  const [view, setView] = useState<View>("tasks");
  const [selectedTask, setSelectedTask] = useState<MyQueueTask | null>(null);
  // A single shared timeclock controller so the ClockBar mini-widget and the
  // Time Clock view stay in sync — punching in/out on one updates both (the
  // prototype used one shared `mqTimeclockStatus` global for the same reason).
  const timeclock = useMyQueueTimeclock();

  // AIVA-7519: the timeclock controller lives at the panel level (above the tab
  // bar) and — unlike the Tasks/QC views, which remount and re-fetch on every
  // tab switch — it only fetches on mount and never polls. So a status change
  // made in Engage stayed stale until the operator left MyQueue entirely and
  // came back. Re-fetch it on each tab switch so the "Current Status" (and the
  // always-visible ClockBar) reflect the latest state as soon as tabs toggle.
  // `refresh` guards on `connected` and never rejects (see useMyQueue.ts).
  const { refresh: refreshTimeclock } = timeclock;
  const selectView = useCallback(
    (next: View) => {
      setView(next);
      setSelectedTask(null);
      void refreshTimeclock();
    },
    [refreshTimeclock],
  );

  // A Navigate-to-MyQueue record from an Actions-tab card (shared/inAppLinks):
  // the task id or job number to open. Claimed in an effect, never a useState
  // initializer, so StrictMode's discarded render cannot eat the park.
  const [pendingTaskRef, setPendingTaskRef] = useState<string | null>(null);
  useEffect(() => {
    const open = (record: string | undefined) => {
      if (!record) return;
      selectView("tasks");
      setPendingTaskRef(record);
    };
    open(consumePendingInAppNav("myqueue")?.record);
    return subscribeInAppNav((target) => {
      if (target.tab !== "myqueue" || !target.record) return;
      consumePendingInAppNav("myqueue");
      open(target.record);
    });
  }, [selectView]);
  const clearPendingTaskRef = useCallback(() => setPendingTaskRef(null), []);

  return (
    <div className={styles.panel}>
      <ModuleHeader
        title="MyQueue"
        subtitle="Operator task queue & timeclock"
        onBack={onBack}
      />
      <ClockBar timeclock={timeclock} />
      <div className={styles.tabBar}>
        {(["tasks", "timeclock", "qc", "invoices"] as View[]).map((v) => (
          <button
            key={v}
            type="button"
            className={`${styles.tab} ${view === v ? styles.tabActive : ""}`}
            onClick={() => selectView(v)}
          >
            {getViewLabel(v)}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        {view === "tasks" && (
          <TasksView
            selectedTask={selectedTask}
            onSelectTask={setSelectedTask}
            pendingTaskRef={pendingTaskRef}
            onPendingTaskHandled={clearPendingTaskRef}
          />
        )}
        {view === "timeclock" && <TimeclockView timeclock={timeclock} />}
        {view === "qc" && <QcView />}
        {view === "invoices" && <InvoicesView />}
      </div>
    </div>
  );
}

/* ── Tasks View ────────────────────────────── */

interface TasksViewProps {
  readonly selectedTask: MyQueueTask | null;
  readonly onSelectTask: (t: MyQueueTask | null) => void;
  /** Task id or job number to open once the list has loaded. */
  readonly pendingTaskRef?: string | null;
  readonly onPendingTaskHandled?: () => void;
}

function TasksView({ selectedTask, onSelectTask, pendingTaskRef, onPendingTaskHandled }: TasksViewProps) {
  const {
    filter,
    setFilter,
    page,
    setPage,
    pageSize,
    tasks,
    inProgressTasks,
    totalCount,
    loading,
    error,
    refresh,
  } = useMyQueueTasks();

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // TaskDetail runs off a list row (its Start/Complete actions need the row's
  // jobNo and state), and MyQueue has no get-task-by-id call — so a requested
  // task is opened by finding it in a freshly loaded list, not the list that
  // happened to be on screen when the request arrived.
  const sawPendingLoadRef = useRef(false);
  useEffect(() => {
    if (!pendingTaskRef) return;
    sawPendingLoadRef.current = false;
    refresh().catch(() => undefined); // load() records its own failure in `error`
  }, [pendingTaskRef, refresh]);
  useEffect(() => {
    if (!pendingTaskRef) return;
    if (loading) {
      sawPendingLoadRef.current = true;
      return;
    }
    if (!sawPendingLoadRef.current) return;
    const match = [...inProgressTasks, ...tasks].find(
      (t) => String(t.id) === pendingTaskRef || t.jobNo === pendingTaskRef,
    );
    if (match) {
      onSelectTask(match);
    } else if (error) {
      emitToast({ title: `Couldn't load MyQueue to open task ${pendingTaskRef}.`, intent: "error" });
    } else {
      emitToast({ title: `Couldn't find task ${pendingTaskRef} in your MyQueue list.`, intent: "error" });
    }
    onPendingTaskHandled?.();
  }, [pendingTaskRef, loading, error, tasks, inProgressTasks, onSelectTask, onPendingTaskHandled]);

  const reminders = useMemo(
    () => computeReminders(tasks, inProgressTasks),
    [tasks, inProgressTasks],
  );

  const stats = useMemo(() => {
    let outstanding = 0;
    let overdue = 0;
    const now = Date.now();
    for (const t of tasks) {
      if (!t.completed && !t.started) outstanding++;
      if (t.dueDate && !t.completed) {
        const due = Date.parse(t.dueDate);
        if (Number.isFinite(due) && due < now) overdue++;
      }
    }
    return {
      outstanding,
      inProgress: inProgressTasks.length,
      overdue,
    };
  }, [tasks, inProgressTasks]);

  if (selectedTask) {
    return (
      <TaskDetail
        task={selectedTask}
        onBack={() => onSelectTask(null)}
        onListRefresh={() => {
          refresh().catch(() => undefined);
        }}
      />
    );
  }

  return (
    <>
      <ReminderBanner reminders={reminders} />

      <div className={styles.mqStats}>
        <div className={styles.mqStat}>
          <div className={styles.mqStatVal}>{stats.outstanding}</div>
          <div className={styles.mqStatLbl}>Outstanding</div>
        </div>
        <div className={styles.mqStat}>
          <div className={styles.mqStatVal}>{stats.inProgress}</div>
          <div className={styles.mqStatLbl}>In Progress</div>
        </div>
        <div className={styles.mqStat}>
          <div
            className={`${styles.mqStatVal} ${stats.overdue > 0 ? styles.mqStatValDanger : ""}`}
          >
            {stats.overdue}
          </div>
          <div className={styles.mqStatLbl}>Overdue</div>
        </div>
      </div>

      <div className={styles.chips}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`${styles.chip} ${filter === f.id ? styles.chipActive : ""}`}
            onClick={() => {
              setFilter(f.id);
              setPage(1);
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {loading && tasks.length === 0 && <SkeletonCards label="Loading tasks…" />}

      {!loading && tasks.length === 0 && (
        <div className={styles.empty}>No tasks to show</div>
      )}

      {tasks.length > 0 && (
        <div className={styles.taskList}>
          {tasks.map((task) => {
            const status = getStatusLabel(task);
            return (
              <button
                key={task.id}
                type="button"
                className={styles.taskCard}
                onClick={() => onSelectTask(task)}
              >
                <span className={styles.taskHd}>
                  {task.jobNo && <span className={styles.jobNo}>#{task.jobNo}</span>}
                  <span className={styles.taskName}>
                    {task.jobTask ?? task.title ?? "Untitled Task"}
                  </span>
                  {(task.vip || task.rush || task.priority) && (
                    <span className={styles.badges}>
                      {task.vip && <span className={`${styles.badge} ${styles.badgeVip}`}>VIP</span>}
                      {task.rush && (
                        <span className={`${styles.badge} ${styles.badgeRush}`}>RUSH</span>
                      )}
                      {task.priority && (
                        <span className={`${styles.badge} ${styles.badgePriority}`}>PRIORITY</span>
                      )}
                    </span>
                  )}
                </span>
                <span className={styles.taskMeta}>
                  {status && (
                    <span className={`${styles.taskStatus} ${status.className}`}>
                      {status.label}
                    </span>
                  )}
                  {task.requestedBy && <span>{task.requestedBy}</span>}
                  {(task.displayDueDate ?? task.dueDate) && (
                    <span className={`${styles.due} ${dueUrgencyClass(task)}`}>
                      Due: {task.displayDueDate ?? task.dueDate}
                    </span>
                  )}
                  {task.estimatedHours != null && task.estimatedHours !== "" && (
                    <span>{String(task.estimatedHours)}h est</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            « Prev
          </button>
          <span>
            Page {page} of {totalPages} ({totalCount} task{totalCount === 1 ? "" : "s"})
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            Next »
          </button>
        </div>
      )}
    </>
  );
}

/* ── Task Detail ───────────────────────────── */

/**
 * Turn a raw task-action failure into a friendly message and detect whether
 * the backend says the task is already started/completed. serviceFetch throws
 * `MyQueue POST /tasks/start: 400 {json}`, so we pull the JSON tail and read
 * its `message` / `detailedMessages` rather than surfacing the raw string.
 */
function describeTaskActionError(raw: string): {
  message: string | null;
  alreadyStarted: boolean;
  alreadyCompleted: boolean;
} {
  let backendMsg = "";
  let detailed: string[] = [];
  const brace = raw.indexOf("{");
  if (brace >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(brace)) as {
        message?: string;
        detailedMessages?: string[];
      };
      backendMsg = parsed.message ?? "";
      detailed = parsed.detailedMessages ?? [];
    } catch {
      // Body wasn't JSON — fall back to the generic message.
    }
  }
  const haystack = `${backendMsg} ${detailed.join(" ")}`.toLowerCase();
  const alreadyCompleted = haystack.includes("already completed");
  const alreadyStarted = haystack.includes("already started");
  let message: string | null = null;
  if (alreadyCompleted) message = "This task has already been completed.";
  else if (alreadyStarted) message = "This task has already been started.";
  return { message, alreadyStarted, alreadyCompleted };
}

interface TaskDetailProps {
  readonly task: MyQueueTask;
  readonly onBack: () => void;
  readonly onListRefresh: () => void;
}

function TaskDetail({ task, onBack, onListRefresh }: TaskDetailProps) {
  const {
    data,
    loading: detailLoading,
    fetchDetails,
    updateComment,
    runAction,
  } = useMyQueueTaskDetail();
  const [comment, setComment] = useState<string>("");
  const [busy, setBusy] = useState<string | null>(null);
  const [savingComment, setSavingComment] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Track started/completed locally so the action buttons flip in place after
  // an action (the prototype's mqTaskAction stayed on the detail and re-rendered
  // the buttons rather than kicking the user back to the list).
  const [started, setStarted] = useState<boolean>(!!task.started);
  const [completed, setCompleted] = useState<boolean>(!!task.completed);

  useEffect(() => {
    setStarted(!!task.started);
    setCompleted(!!task.completed);
  }, [task.id, task.started, task.completed]);

  useEffect(() => {
    fetchDetails(task.id).catch(() => undefined);
  }, [fetchDetails, task.id]);

  useEffect(() => {
    if (data) setComment(data.comments ?? "");
  }, [data]);

  async function handleAction(action: "start" | "pause" | "complete") {
    setBusy(action);
    setError(null);
    try {
      await runAction(action, task.jobNo ?? "", task.id);
      // Optimistically reflect the new state so the buttons update without
      // leaving the detail view; refresh the underlying list in the background.
      if (action === "start") {
        setStarted(true);
        setCompleted(false);
      } else if (action === "pause") {
        setStarted(false);
        setCompleted(false);
      } else if (action === "complete") {
        setStarted(true);
        setCompleted(true);
      }
      onListRefresh();
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      // The backend rejects starting a task that's already started/completed
      // (e.g. after Start → Pause → Start). Show a friendly message — never
      // the raw "MyQueue POST /tasks/start: 400 {…}" — and re-sync the button
      // state to what the backend reports so the invalid action disappears.
      const info = describeTaskActionError(raw);
      if (info.alreadyCompleted) {
        setStarted(true);
        setCompleted(true);
      } else if (info.alreadyStarted) {
        setStarted(true);
        setCompleted(false);
      }
      setError(info.message ?? `Couldn't ${action} the task. Please try again.`);
      onListRefresh();
    } finally {
      setBusy(null);
    }
  }

  async function handleSaveComment() {
    setSavingComment(true);
    setError(null);
    setFeedback(null);
    try {
      await updateComment(task.id, comment);
      setFeedback("Comment saved");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save comment";
      setError(msg);
    } finally {
      setSavingComment(false);
    }
  }

  return (
    <div className={styles.card}>
      <button type="button" className={styles.detailBack} onClick={onBack}>
        ← Back to tasks
      </button>

      <h3 className={styles.detailTitle}>
        {task.jobNo ? `#${task.jobNo} — ` : ""}
        {task.jobTask ?? task.title ?? "Task"}
      </h3>

      <div className={styles.detailGrid}>
        <Field label="Job Number" value={task.jobNo ?? "—"} />
        <Field label="Task" value={task.jobTask ?? task.title ?? "—"} />
        <Field label="Requested By" value={task.requestedBy ?? "—"} />
        <Field label="Site" value={task.site ?? ""} />
        {(task.displayDueDate ?? task.dueDate) && (
          <Field label="Due Date" value={task.displayDueDate ?? task.dueDate ?? ""} />
        )}
        {task.displayScheduledDate && (
          <Field label="Scheduled" value={task.displayScheduledDate} />
        )}
        {task.displayStartedDate && (
          <Field label="Started" value={task.displayStartedDate} />
        )}
        {task.displayCompletedDate && (
          <Field label="Completed" value={task.displayCompletedDate} />
        )}
        {task.estimatedHours != null && task.estimatedHours !== "" && (
          <Field label="Estimated Hours" value={String(task.estimatedHours)} />
        )}
      </div>

      {(task.vip || task.rush || task.priority) && (
        <div className={styles.badges}>
          {task.vip && <span className={`${styles.badge} ${styles.badgeVip}`}>VIP</span>}
          {task.rush && <span className={`${styles.badge} ${styles.badgeRush}`}>RUSH</span>}
          {task.priority && (
            <span className={`${styles.badge} ${styles.badgePriority}`}>PRIORITY</span>
          )}
        </div>
      )}
      {task.vip && task.vipNotes && (
        <div className={styles.vipNotes}>VIP Notes: {task.vipNotes}</div>
      )}

      {detailLoading && !data && <LoadingRow label="Loading details…" />}

      {data?.instructions && (
        <InstructionCard label="Instructions" value={data.instructions} />
      )}
      {data?.taskInstructions && (
        <InstructionCard label="Task Instructions" value={data.taskInstructions} />
      )}
      {(data?.billingCode1 || data?.billingCode2) && (
        <div className={styles.detailGrid}>
          {data?.billingCode1 && <Field label="Billing Code 1" value={data.billingCode1} />}
          {data?.billingCode2 && <Field label="Billing Code 2" value={data.billingCode2} />}
        </div>
      )}
      {data?.taskNotes && data.taskNotes.length > 0 && (
        <NotesCard notes={data.taskNotes} />
      )}

      <div className={styles.detailActions}>
        {!started && !completed && (
          <button
            type="button"
            className={styles.btnStart}
            onClick={() => handleAction("start")}
            disabled={busy !== null}
          >
            {busy === "start" ? "Starting…" : "▶ Start"}
          </button>
        )}
        {started && !completed && (
          <>
            <button
              type="button"
              className={styles.btnPause}
              onClick={() => handleAction("pause")}
              disabled={busy !== null}
            >
              {busy === "pause" ? "Pausing…" : "⏸ Pause"}
            </button>
            <button
              type="button"
              className={styles.btnComplete}
              onClick={() => handleAction("complete")}
              disabled={busy !== null}
            >
              {busy === "complete" ? "Completing…" : "✓ Complete"}
            </button>
          </>
        )}
      </div>

      <div className={styles.row}>
        <label className={styles.label} htmlFor="mq-comment">Comments</label>
        <textarea
          id="mq-comment"
          className={styles.textArea}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment…"
        />
        <button
          type="button"
          className={styles.btnSaveComment}
          onClick={handleSaveComment}
          disabled={savingComment}
        >
          {savingComment ? "Saving…" : "Save comment"}
        </button>
      </div>

      {feedback && <p className={styles.note}>{feedback}</p>}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

function Field({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue} style={{ whiteSpace: "pre-wrap" }}>
        {value}
      </div>
    </div>
  );
}

/** Strip event handlers and javascript:/data: URLs from a single element. */
function cleanInstructionAttributes(el: Element): void {
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith("on")) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === "href" || name === "src") {
      const val = attr.value.trim().toLowerCase();
      if (val.startsWith("javascript:") || val.startsWith("data:")) {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/**
 * Sanitize the Engage instructions HTML for safe rendering in the popup —
 * DOMParser does NOT execute scripts, and we then strip dangerous elements,
 * event handlers, and javascript:/data: URLs. Inline `style` attributes are
 * kept so the original colours/formatting survive.
 */
function sanitizeInstructionHtml(html: string): string {
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("script, style, iframe, object, embed, link, meta, base, form, input")
    .forEach((el) => el.remove());
  doc.querySelectorAll("*").forEach(cleanInstructionAttributes);
  doc.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  return doc.body.innerHTML;
}

/**
 * Generic popup built on the native <dialog> element — no ARIA-role or
 * click-on-backdrop shims (which trip accessibility rules). The browser
 * supplies the modal backdrop and Escape-to-dismiss; `onClose` fires on both
 * Escape and the Close button.
 */
function Modal({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || el.open) return;
    if (typeof el.showModal === "function") {
      try {
        el.showModal();
      } catch {
        el.open = true;
      }
    } else {
      el.open = true;
    }
  }, []);

  return (
    <dialog ref={ref} className={styles.modal} aria-label={title} onClose={onClose}>
      <div className={styles.modalHd}>
        <span className={styles.modalTitle}>{title}</span>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className={styles.modalBody}>{children}</div>
    </dialog>
  );
}

/** Popup rendering the sanitized instructions HTML with its colours. */
function InstructionModal({
  label,
  html,
  onClose,
}: {
  readonly label: string;
  readonly html: string;
  readonly onClose: () => void;
}) {
  const safeHtml = useMemo(() => sanitizeInstructionHtml(html), [html]);
  return (
    <Modal title={label} onClose={onClose}>
      {/* Content is sanitized by sanitizeInstructionHtml above. */}
      <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </Modal>
  );
}

/** A card that shows an instruction field's label + a "View" button. */
function InstructionCard({ label, value }: { readonly label: string; readonly value: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.instrCard}>
      <div className={styles.instrCardHd}>
        <span className={styles.label}>{label}</span>
        <button
          type="button"
          className={styles.instrViewBtn}
          onClick={() => setOpen(true)}
        >
          View
        </button>
      </div>
      {open && (
        <InstructionModal label={label} html={value} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

type TaskNote = { createdBy?: string; created?: string; comments?: string };

/** Popup listing every task note, each comment rendered as sanitized HTML. */
function NotesModal({
  notes,
  onClose,
}: {
  readonly notes: readonly TaskNote[];
  readonly onClose: () => void;
}) {
  return (
    <Modal title="Notes" onClose={onClose}>
      {notes.map((n, i) => (
        <div key={`${n.created ?? ""}-${n.createdBy ?? ""}-${i}`} className={styles.noteItem}>
          <div className={styles.noteMeta}>
            {n.createdBy ?? ""}
            {n.created ? ` — ${n.created}` : ""}
          </div>
          {/* Comment HTML sanitized so colours/formatting survive. */}
          <div dangerouslySetInnerHTML={{ __html: sanitizeInstructionHtml(n.comments ?? "") }} />
        </div>
      ))}
    </Modal>
  );
}

/** A card showing a "Notes" label + a "View" button that opens all notes. */
function NotesCard({ notes }: { readonly notes: readonly TaskNote[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.instrCard}>
      <div className={styles.instrCardHd}>
        <span className={styles.label}>Notes</span>
        <button
          type="button"
          className={styles.instrViewBtn}
          onClick={() => setOpen(true)}
        >
          View
        </button>
      </div>
      {open && <NotesModal notes={notes} onClose={() => setOpen(false)} />}
    </div>
  );
}

/* ── Timeclock Mini-bar ─────────────────────── */

function statusFromState(state: TimeclockState): {
  label: string;
  dot: string;
  next: MyQueueTimeclockAction;
} {
  switch (state) {
    case "in":
      return { label: "Punched In", dot: styles.clockDotIn!, next: "punchout" };
    case "lunch":
      return { label: "On Lunch", dot: styles.clockDotBreak!, next: "return" };
    case "break":
      return { label: "On Break", dot: styles.clockDotBreak!, next: "return" };
    case "out":
    default:
      return { label: "Punched Out", dot: "", next: "punchin" };
  }
}

type TimeclockController = ReturnType<typeof useMyQueueTimeclock>;

function ClockBar({ timeclock }: { readonly timeclock: TimeclockController }) {
  const { state, performAction, acting } = timeclock;
  const status = statusFromState(state);

  return (
    <div className={styles.clockBar}>
      <span className={`${styles.clockDot} ${status.dot}`} />
      <span className={styles.clockStatus}>{status.label}</span>
      <button
        type="button"
        className={styles.clockBtn}
        onClick={() => {
          performAction(status.next).catch(() => undefined);
        }}
        disabled={acting}
      >
        {acting ? "…" : ACTION_LABELS[status.next]}
      </button>
    </div>
  );
}

/* ── Timeclock View ─────────────────────────── */

function TimeclockView({ timeclock }: { readonly timeclock: TimeclockController }) {
  const { data, state, loading, error, acting, performAction } = timeclock;
  const isIn = state === "in";
  const isOut = state === "out";
  const isLunchBreak = state === "lunch" || state === "break";
  // Derive the label from the resolved punch state (Clocked In/Out/On Lunch/
  // On Break) rather than the raw `lastAction` string, which can surface
  // unmapped values like "checkout".
  const stateLabel = statusFromState(state).label;
  const entries = data?.timeclockEntries ?? [];

  return (
    <>
      <div className={styles.tcStatus}>
        <p className={styles.tcStatusLabel}>Current Status</p>
        {loading && !data ? (
          <LoadingRow label="Loading…" />
        ) : (
          <>
            <p className={styles.tcStatusState}>{stateLabel}</p>
            {data?.lastTime && (
              <p className={styles.tcStatusTime}>Since: {data.lastTime}</p>
            )}
            {data?.userName && (
              <p className={styles.tcStatusTime}>
                {data.userName}
                {data.role ? ` (${data.role})` : ""}
              </p>
            )}
          </>
        )}
        {error && <p className={styles.error}>{error}</p>}
      </div>

      <div className={styles.tcActions}>
        <button
          type="button"
          className={styles.tcBtnPrimary}
          disabled={!isOut || acting}
          onClick={() => {
            performAction("punchin").catch(() => undefined);
          }}
        >
          Punch In
        </button>
        <button
          type="button"
          className={styles.tcBtnAmber}
          disabled={!isIn || acting}
          onClick={() => {
            performAction("lunch").catch(() => undefined);
          }}
        >
          Lunch
        </button>
        <button
          type="button"
          className={styles.tcBtnAmber}
          disabled={!isIn || acting}
          onClick={() => {
            performAction("break").catch(() => undefined);
          }}
        >
          Break
        </button>
        <button
          type="button"
          className={styles.tcBtnReturn}
          disabled={!isLunchBreak || acting}
          onClick={() => {
            performAction("return").catch(() => undefined);
          }}
        >
          Return
        </button>
        <button
          type="button"
          className={styles.tcBtnDanger}
          disabled={!isIn || acting}
          onClick={() => {
            performAction("punchout").catch(() => undefined);
          }}
        >
          Punch Out
        </button>
      </div>

      <div className={styles.tcEntries}>
        {entries.length === 0 ? (
          <p className={styles.tcEmpty}>No entries today</p>
        ) : (
          entries.map((e, i) => (
            <div
              key={`${e.startTime ?? ""}-${e.action ?? e.tcitype ?? ""}-${i}`}
              className={styles.tcEntry}
            >
              <div className={styles.tcEntryType}>
                {e.tcitype ?? e.action ?? ""}
              </div>
              <div className={styles.tcEntryTime}>
                {(e.displayStartTime ?? e.startTime ?? "") +
                  ((e.displayEndTime ?? e.endTime)
                    ? ` - ${e.displayEndTime ?? e.endTime}`
                    : "")}
              </div>
              <div className={styles.tcEntryHours}>
                {e.hours == null ? "" : `${e.hours.toFixed(2)}h`}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/* ── QC View ────────────────────────────────── */

function QcView() {
  const { tasks, inProgressTasks, loading: tasksLoading } = useMyQueueTasks();
  const {
    qcInfo,
    prodTask,
    loading,
    error,
    submitting,
    fetchQcInfo,
    fetchProdTask,
    submitQc,
    clearProdTask,
    clearQcInfo,
  } = useMyQueueQc();
  const [feedback, setFeedback] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const active = tasks.filter((t) => t.started && !t.completed);
    const seen = new Set<number>();
    const out: MyQueueTask[] = [];
    for (const t of [...active, ...inProgressTasks]) {
      if (!seen.has(t.id)) {
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  }, [tasks, inProgressTasks]);

  async function handleDecision(pass: boolean) {
    setFeedback(null);
    try {
      await submitQc(pass);
      setFeedback(pass ? "QC passed" : "QC failed — rework needed");
      clearProdTask();
    } catch {
      // submitQc records the error via the hook's `error` state
    }
  }

  if (prodTask) {
    return (
      <ProdTaskDetail
        prod={prodTask}
        submitting={submitting}
        onBack={clearProdTask}
        onPass={() => handleDecision(true)}
        onFail={() => handleDecision(false)}
        feedback={feedback}
        error={error}
      />
    );
  }

  if (qcInfo) {
    return (
      <div className={styles.card}>
        <button
          type="button"
          className={styles.detailBack}
          onClick={() => {
            setFeedback(null);
            clearQcInfo();
          }}
        >
          ← Back to QC list
        </button>

        <div className={styles.detailGrid}>
          {qcInfo.jobNo && <Field label="Job Number" value={qcInfo.jobNo} />}
          {qcInfo.jobTask && <Field label="Task" value={qcInfo.jobTask} />}
          {qcInfo.requestedBy && (
            <Field label="Requested By" value={qcInfo.requestedBy} />
          )}
          {qcInfo.office && <Field label="Office" value={qcInfo.office} />}
          {qcInfo.displayDueDate && (
            <Field label="Due Date" value={qcInfo.displayDueDate} />
          )}
          {qcInfo.estimatedHours != null && qcInfo.estimatedHours !== "" && (
            <Field
              label="Estimated Hours"
              value={String(qcInfo.estimatedHours)}
            />
          )}
          {qcInfo.billingCodes && (
            <Field label="Billing Codes" value={qcInfo.billingCodes} />
          )}
          {qcInfo.noPages != null && (
            <Field label="Pages" value={String(qcInfo.noPages)} />
          )}
        </div>

        {loading && <LoadingRow label="Loading QC task…" />}
        {!loading && (qcInfo.productionsTaskForQc ?? []).length > 0 && (
          <>
            <p className={styles.label}>Production Tasks</p>
            <div className={styles.taskList}>
              {(qcInfo.productionsTaskForQc ?? []).map((pt) => {
                const status = (pt.qcStatus ?? "").toLowerCase();
                const statusCls = qcStatusClass(status);
                return (
                <button
                  key={pt.id}
                  type="button"
                  className={styles.taskCard}
                  onClick={() => {
                    fetchProdTask(pt).catch(() => undefined);
                  }}
                >
                  <span className={styles.taskHd}>
                    <span className={styles.taskName}>
                      {pt.jobTask ?? "Untitled"}
                    </span>
                    <span className={`${styles.taskStatus} ${statusCls}`}>
                      {pt.qcStatus ?? "Pending"}
                    </span>
                  </span>
                  <span className={styles.taskMeta}>
                    {(pt.firstName || pt.lastName) && (
                      <span>{`${pt.firstName ?? ""} ${pt.lastName ?? ""}`.trim()}</span>
                    )}
                    {pt.displayProductionStartedAt && (
                      <span>Start: {pt.displayProductionStartedAt}</span>
                    )}
                    {pt.displayProductionCompletedAt && (
                      <span>End: {pt.displayProductionCompletedAt}</span>
                    )}
                  </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {error && <p className={styles.error}>{error}</p>}
        {feedback && <p className={styles.note}>{feedback}</p>}
      </div>
    );
  }

  // Skeleton only while the candidate list itself is loading. A card click
  // (QC lookup in flight → `loading`) shows a centred spinner instead of
  // turning the list back into skeletons; the cards are hidden either way so a
  // stale card can't be re-clicked.
  const showList = !tasksLoading && !loading;

  return (
    <>
      <p className={styles.label}>Active Tasks — Select to view QC</p>
      {error && <p className={styles.error}>{error}</p>}
      {tasksLoading && <SkeletonCards count={3} label="Loading QC tasks…" />}
      {!tasksLoading && loading && <LoadingRow label="Loading QC…" />}
      {showList && candidates.length === 0 && (
        <div className={styles.empty}>No active tasks for QC</div>
      )}
      {showList && candidates.length > 0 && (
        <div className={styles.taskList}>
          {candidates.map((t) => (
            <button
              key={t.id}
              type="button"
              className={styles.taskCard}
              onClick={() => {
                fetchQcInfo(t.id).catch(() => undefined);
              }}
            >
              <span className={styles.taskHd}>
                {t.jobNo && (
                  <span className={styles.jobNo}>#{t.jobNo}</span>
                )}
                <span className={styles.taskName}>
                  {t.jobTask ?? t.title ?? "Untitled"}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

interface ProdTaskDetailProps {
  readonly prod: import("../services/myqueueApi").MyQueueProdTaskData;
  readonly submitting: boolean;
  readonly onBack: () => void;
  readonly onPass: () => void;
  readonly onFail: () => void;
  readonly feedback: string | null;
  readonly error: string | null;
}

function ProdTaskDetail({
  prod,
  submitting,
  onBack,
  onPass,
  onFail,
  feedback,
  error,
}: ProdTaskDetailProps) {
  const status = (prod.qcStatus ?? "").toLowerCase();
  const decided = status === "passed" || status === "failed";
  return (
    <div className={styles.card}>
      <button type="button" className={styles.detailBack} onClick={onBack}>
        ← Back to QC
      </button>

      <div className={styles.detailGrid}>
        {prod.jobTask && <Field label="Task" value={prod.jobTask} />}
        {prod.jobNo && <Field label="Job No" value={prod.jobNo} />}
        {prod.jobType && <Field label="Job Type" value={prod.jobType} />}
        <Field label="QC Status" value={prod.qcStatus ?? "Pending"} />
        {prod.displayStartTime && (
          <Field label="Start" value={prod.displayStartTime} />
        )}
        {prod.displayStopTime && (
          <Field label="Stop" value={prod.displayStopTime} />
        )}
        {prod.totalActualHours != null && (
          <Field
            label="Actual Hours"
            value={prod.totalActualHours.toFixed(2)}
          />
        )}
      </div>

      {prod.instructions?.requestInstructions && (
        <InstructionCard
          label="Request Instructions"
          value={prod.instructions.requestInstructions}
        />
      )}
      {prod.instructions?.taskInstructions && (
        <InstructionCard
          label="Task Instructions"
          value={prod.instructions.taskInstructions}
        />
      )}

      {(prod.productionsTasks ?? []).length > 0 && (
        <div>
          <p className={styles.label}>Time Blocks</p>
          {(prod.productionsTasks ?? []).map((tb, i) => (
            <div
              key={`${tb.displayStartTime ?? ""}-${i}`}
              className={styles.tcEntry}
            >
              <div className={styles.tcEntryType}>
                {`${tb.firstName ?? ""} ${tb.lastName ?? ""}`.trim()}
              </div>
              <div className={styles.tcEntryTime}>
                {(tb.displayStartTime ?? "") +
                  (tb.displayStopTime ? ` - ${tb.displayStopTime}` : "")}
              </div>
              <div className={styles.tcEntryHours}>
                {tb.actualhours == null ? "" : `${tb.actualhours.toFixed(2)}h`}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className={styles.label}>QC Decision</p>
      <div className={styles.qcDecisionRow}>
        <button
          type="button"
          className={styles.qcBtnPass}
          onClick={onPass}
          disabled={submitting || decided}
        >
          {submitting ? "…" : "✓ Pass"}
        </button>
        <button
          type="button"
          className={styles.qcBtnFail}
          onClick={onFail}
          disabled={submitting || decided}
        >
          {submitting ? "…" : "✗ Fail"}
        </button>
      </div>

      {feedback && <p className={styles.note}>{feedback}</p>}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/* ── Invoices View ──────────────────────────── */

function InvoicesView() {
  const [jobNo, setJobNo] = useState("");
  const { data, loading, error, search } = useMyQueueInvoices();

  const months = useMemo(() => data?.invoicesByMonth ?? [], [data]);

  // Debounced auto-search as the operator types (prototype used a 500ms
  // debounce). The explicit Search button and Enter key still work below.
  useEffect(() => {
    const trimmed = jobNo.trim();
    if (!trimmed) return undefined;
    const id = setTimeout(() => {
      search(trimmed).catch(() => undefined);
    }, 500);
    return () => clearTimeout(id);
  }, [jobNo, search]);

  return (
    <>
      <div className={styles.card}>
        <input
          className={styles.input}
          type="text"
          aria-label="Job number"
          value={jobNo}
          placeholder="Enter a job number…"
          onChange={(e) => setJobNo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              search(jobNo).catch(() => undefined);
            }
          }}
        />
        {loading && <LoadingRow label="Loading invoices…" />}
        {error && <p className={styles.error}>{error}</p>}
      </div>

      {!jobNo.trim() && !data && (
        <div className={styles.empty}>Enter a job number to view invoices</div>
      )}

      {months.length > 0 && (
        <div className={styles.card}>
          {months.map((m, i) => (
            <div
              key={`${m.month ?? ""}-${m.displayMonth ?? ""}-${i}`}
              className={styles.invoiceMonth}
            >
              <p className={styles.label}>{m.displayMonth ?? m.month ?? ""}</p>
              {(m.items ?? []).map((item, j) => (
                <div
                  key={`${item.costCode ?? ""}-${item.quantity ?? 0}-${j}`}
                  className={styles.invoiceItem}
                >
                  <span className={styles.invoiceCode}>{item.costCode ?? ""}</span>
                  <span>Qty: {item.quantity ?? 0}</span>
                  <span className={styles.invoiceCharge}>
                    {formatCurrency(item.charge)}
                  </span>
                </div>
              ))}
              {m.subtotal && (
                <div className={styles.invoiceItem}>
                  <span className={styles.invoiceCode}>
                    Subtotal Qty: {m.subtotal.totalQuantity ?? 0}
                  </span>
                  <span className={styles.invoiceCharge}>
                    {formatCurrency(m.subtotal.totalCharge)}
                  </span>
                </div>
              )}
            </div>
          ))}
          {data?.grandTotal && (
            <div className={styles.grandTotal}>
              <span>Grand Total — Qty: {data.grandTotal.totalQuantity ?? 0}</span>
              <span>{formatCurrency(data.grandTotal.totalCharge)}</span>
            </div>
          )}
        </div>
      )}

      {!loading && !error && data && months.length === 0 && (
        <div className={styles.empty}>No invoices found for #{jobNo.trim()}</div>
      )}
    </>
  );
}
